const HEX_COLOR_REG_EXP = /^#?([\da-f]{3}|[\da-f]{6})$/i;
const RGB_COLOR_REG_EXP = /^rgba?\((.+)\)$/i;
const HSL_COLOR_REG_EXP = /^hsla?\((.+)\)$/i;

const getColorParts = (value: string) =>
  value
    .replace(/\s*\/\s*/g, ' ')
    .replaceAll(',', ' ')
    .trim()
    .split(/\s+/);

const getHexPart = (value: number) => Math.round(value).toString(16).padStart(2, '0');

const parseAlpha = (value: string | undefined) => {
  if (!value) {
    return true;
  }

  if (value.endsWith('%')) {
    return Number(value.slice(0, -1)) === 100;
  }

  return Number(value) === 1;
};

const parseRgbPart = (value: string | undefined) => {
  if (!value) {
    return null;
  }

  const parsedValue = value.endsWith('%') ? (Number(value.slice(0, -1)) * 255) / 100 : Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 255) {
    return null;
  }

  return parsedValue;
};

const parseHue = (value: string | undefined) => {
  if (!value) {
    return null;
  }

  const parsedValue = value.endsWith('deg') ? Number(value.slice(0, -3)) : Number(value);

  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return ((parsedValue % 360) + 360) % 360;
};

const parsePercent = (value: string | undefined) => {
  if (!value) {
    return null;
  }

  if (!value.endsWith('%')) {
    return null;
  }

  const parsedValue = Number(value.slice(0, -1));

  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 100) {
    return null;
  }

  return parsedValue / 100;
};

const getHexFromRgb = (red: number, green: number, blue: number) =>
  `#${getHexPart(red)}${getHexPart(green)}${getHexPart(blue)}`;

const getHexFromHsl = (hue: number, saturation: number, lightness: number) => {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const secondLargestComponent = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const lightnessMatch = lightness - chroma / 2;

  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue < 60) {
    red = chroma;
    green = secondLargestComponent;
  } else if (hue < 120) {
    red = secondLargestComponent;
    green = chroma;
  } else if (hue < 180) {
    green = chroma;
    blue = secondLargestComponent;
  } else if (hue < 240) {
    green = secondLargestComponent;
    blue = chroma;
  } else if (hue < 300) {
    red = secondLargestComponent;
    blue = chroma;
  } else {
    red = chroma;
    blue = secondLargestComponent;
  }

  return getHexFromRgb((red + lightnessMatch) * 255, (green + lightnessMatch) * 255, (blue + lightnessMatch) * 255);
};

export function normalizeColorToHex(value: string): string | null {
  const color = value.trim().toLowerCase();

  const hexMatch = color.match(HEX_COLOR_REG_EXP);

  if (hexMatch) {
    const hex = hexMatch[1];

    if (hex.length === 3) {
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }

    return `#${hex}`;
  }

  const rgbMatch = color.match(RGB_COLOR_REG_EXP);

  if (rgbMatch) {
    const rgbParts = getColorParts(rgbMatch[1]);

    if (rgbParts.length < 3 || rgbParts.length > 4) {
      return null;
    }

    const [redPart, greenPart, bluePart, alphaPart] = rgbParts;
    const red = parseRgbPart(redPart);
    const green = parseRgbPart(greenPart);
    const blue = parseRgbPart(bluePart);

    if (red === null || green === null || blue === null || !parseAlpha(alphaPart)) {
      return null;
    }

    return getHexFromRgb(red, green, blue);
  }

  const hslMatch = color.match(HSL_COLOR_REG_EXP);

  if (hslMatch) {
    const hslParts = getColorParts(hslMatch[1]);

    if (hslParts.length < 3 || hslParts.length > 4) {
      return null;
    }

    const [huePart, saturationPart, lightnessPart, alphaPart] = hslParts;
    const hue = parseHue(huePart);
    const saturation = parsePercent(saturationPart);
    const lightness = parsePercent(lightnessPart);

    if (hue === null || saturation === null || lightness === null || !parseAlpha(alphaPart)) {
      return null;
    }

    return getHexFromHsl(hue, saturation, lightness);
  }

  return null;
}

export function getTextColorByBackground(hex: string | null): string {
  const color = hex ? normalizeColorToHex(hex) : null;

  if (!color) {
    return 'var(--go-text)';
  }

  const normalizedColor = color.replace('#', '');

  const red = parseInt(normalizedColor.slice(0, 2), 16);
  const green = parseInt(normalizedColor.slice(2, 4), 16);
  const blue = parseInt(normalizedColor.slice(4, 6), 16);

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

  return brightness > 145 ? 'rgba(6, 21, 43, 1)' : 'rgba(255, 255, 255, 1)';
}
